import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibContainer } from '@chewy/kib-layout-react'

import SwapPatient from '~/components/common/dialog/swapProduct/patientStep/SwapPatient'
import SwapProductAlert from '~/components/common/dialog/swapProduct/shared/SwapProductAlert'
import SwapProductDetails from '~/components/common/dialog/swapProduct/shared/SwapProductDetails'
import { AdministrationCourseType } from '~/constants/prescription'
import { getSwapProductTrackingData } from '~/constants/swapProductAnalytics'
import type { Prescription } from '~/types'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'

import { SwapProductDialogSteps } from '../types'
import { sanitizePrescriptionForSwap } from '../utils'

import styles from './SwapPatientStep.module.scss'

interface SwapPatientStepProps {
  onPatientChange?: (patientId: string) => void
  onProceed?: (prescription: Prescription) => void
  prescription: Prescription
}

export function SwapPatientStep({
  prescription,
  onProceed,
  onPatientChange,
}: SwapPatientStepProps) {
  const changeType = 'patient'
  const { t } = useTranslation('Dialogs')
  const [patientId, setPatientId] = useState<string | null>(null)
  const [disableButton, setDisableButton] = useState(true)

  return (
    <div className={styles.container}>
      <SwapProductAlert changeType={changeType} />
      <SwapProductDetails
        prescription={prescription}
        step={SwapProductDialogSteps.CHANGE_PATIENT}
      />
      <SwapPatient
        prescription={prescription}
        onChange={(newPatientId) => {
          setPatientId(newPatientId)
          setDisableButton(false)
          onPatientChange?.(newPatientId)
        }}
      />
      <KibContainer className={styles.buttonWrapper}>
        {onProceed && (
          <KibButtonNew
            disabled={disableButton}
            onClick={() => {
              trackEvent(
                TrackingEvent.SWAP_PATIENT_SEARCH_SELECT_PATIENT,
                getSwapProductTrackingData(
                  'SWAP_PATIENT_SEARCH_SELECT_PATIENT',
                  prescription,
                ),
              )

              const sanitizedPrescription = sanitizePrescriptionForSwap(
                prescription,
                {
                  preserveVariationForCompounding: true,
                },
              )

              const newPrescription: Prescription = {
                ...sanitizedPrescription,
                id: '',
                patientId,
                createdAt: undefined,
                updatedAt: undefined,
                // Clear dosage-specific fields - different patients need different dosages
                quantity: undefined,
                quantityUnitId: undefined,
                administrationQuantity: undefined,
                administrationQuantityUnitId: undefined,
                administrationQuantityCustomUnit: undefined,
                administrationFrequencyId: undefined,
                administrationFrequencyNotes: undefined,
                administrationRouteId: undefined,
                administrationCourseLength: 0,
                administrationCourseType: AdministrationCourseType.AS_NEEDED,
              }
              onProceed(newPrescription)
            }}
          >
            {t('SWAP_PRODUCT_DIALOG.CONTINUE_TO_PRESCRIPTION')}
          </KibButtonNew>
        )}
      </KibContainer>
    </div>
  )
}
